<template>
  <v-dialog v-model="dialog" max-width="700px" @click:outside="closed">
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5">{{ $t('conglomerates.create_agreement') }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">{{ $t('conglomerates.agreement_name') }}*</span>
            <v-text-field
              v-model="form.name"
              filled
              flat
              dense
              :error-messages="nameErrorMessages"
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">{{ $t('conglomerates.agreement_type') }}*</span>
            <v-select
              v-model="form.arrangementType"
              :items="arrangementTypes"
              item-value="value"
              item-text="text"
              filled
              flat
              dense
              :error-messages="arrangementTypeErrorMessages"
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-4">
            <span class="body black--text my-1">
              {{ $t('conglomerates.agreement_total_licenses') }}*
            </span>
            <v-text-field
              v-model.number="form.totalLicenses"
              type="number"
              min="1"
              filled
              flat
              dense
              :error-messages="totalLicensesErrorMessages"
              hide-details="auto"
            />
          </v-row>

          <v-row class="d-flex flex-column mb-2">
            <span class="body black--text my-1">{{ $t('conglomerates.agreement_dates') }}*</span>
            <v-col>
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.startDate"
                    :label="$t('conglomerates.start_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :error-messages="startDateErrorMessages"
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.startDate" @input="menuStartDate = false" />
              </v-menu>
            </v-col>
            <v-col>
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.endDate"
                    :label="$t('conglomerates.end_date')"
                    prepend-icon="mdi-calendar"
                    readonly
                    :error-messages="endDateErrorMessages"
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.endDate" @input="menuEndDate = false" />
              </v-menu>
            </v-col>
            <span v-if="showErrors && dateError" class="red--text caption ml-4">
              {{ $t('general.valid_dates') }}
            </span>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="px-6 pb-6">
        <v-spacer />
        <v-btn text @click="closed">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="deep-purple" dark @click="save">{{ $t('common.create') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import SnackbarMixin from '@/mixins/snackbar';
import schoolArrangementService from '@/services/schoolArrangement.service';

export default {
  name: 'CreateArrangementModal',
  mixins: [SnackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    schoolGroupId: { type: [String, Number], default: null },
    schoolId: { type: [String, Number], default: null },
  },
  data: () => ({
    loading: false,
    showErrors: false,
    menuStartDate: false,
    menuEndDate: false,
    form: {
      name: '',
      arrangementType: '',
      totalLicenses: null,
      startDate: '',
      endDate: '',
    },
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    arrangementTypes() {
      return [
        { text: this.$t('conglomerates.type_demo'), value: 'Demo' },
        { text: this.$t('conglomerates.type_trial'), value: 'Trial' },
        { text: this.$t('conglomerates.type_client'), value: 'Client' },
      ];
    },
    dateError() {
      if (!this.form.startDate || !this.form.endDate) return false;
      // Dates come from v-date-picker in YYYY-MM-DD format, so string compare is safe.
      return this.form.endDate <= this.form.startDate;
    },
    nameErrorMessages() {
      if (!this.showErrors || this.form.name) return [];
      return [this.$t('general.is_required')];
    },
    arrangementTypeErrorMessages() {
      if (!this.showErrors || this.form.arrangementType) return [];
      return [this.$t('general.is_required')];
    },
    totalLicensesErrorMessages() {
      if (!this.showErrors) return [];
      if (!this.form.totalLicenses) return [this.$t('general.is_required')];
      if (this.form.totalLicenses <= 0) return [this.$t('general.only_positive_number')];
      return [];
    },
    startDateErrorMessages() {
      if (!this.showErrors || this.form.startDate) return [];
      return [this.$t('general.is_required')];
    },
    endDateErrorMessages() {
      if (!this.showErrors || this.form.endDate) return [];
      return [this.$t('general.is_required')];
    },
  },
  methods: {
    closed() {
      this.dialog = false;
      this.showErrors = false;
      this.form = {
        name: '',
        arrangementType: '',
        totalLicenses: null,
        startDate: '',
        endDate: '',
      };
    },
    async save() {
      this.showErrors = true;

      if (
        !this.form.name
        || !this.form.arrangementType
        || !this.form.totalLicenses
        || this.form.totalLicenses <= 0
        || !this.form.startDate
        || !this.form.endDate
        || this.dateError
      ) {
        this.snackbarError(this.$t('conglomerates.error_validation'));
        return;
      }

      if (!this.schoolGroupId && !this.schoolId) {
        this.snackbarError(this.$t('conglomerates.error_validation'));
        return;
      }

      this.loading = true;
      try {
        const payload = {
          name: this.form.name,
          arrangementType: this.form.arrangementType,
          totalLicenses: this.form.totalLicenses,
          startDate: this.form.startDate,
          endDate: this.form.endDate,
        };

        // Add schoolGroupId or schoolId based on what was provided
        if (this.schoolGroupId) {
          payload.schoolGroupId = Number(this.schoolGroupId);
        } else if (this.schoolId) {
          payload.schoolId = Number(this.schoolId);
        }

        const res = await schoolArrangementService.create(payload);

        if (res?.status === 'OK') {
          this.snackbarSuccess(this.$t('conglomerates.save_success'));
          this.$emit('created');
          this.closed();
        } else {
          this.snackbarError(this.$t('conglomerates.error_saving'));
        }
      } catch (error) {
        this.snackbarError(this.$t('conglomerates.error_saving'));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
